import { S } from './app_state.js?v=20260817c';
import { cloneSystemData } from '../../plugin-contracts/system_data_contract.mjs';

const claims = new Map();
const identifier = /^[A-Za-z][A-Za-z0-9_]*$/;
const forbidden = new Set(['items', 'obstacleIds', 'multiSelection', 'constructor', 'prototype', '__proto__']);
const normalized = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const freeze = value => {
  if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); }
  return value;
};

export function systemStateNamespace(manifest, requested) {
  if (!requested) return normalized(manifest.systemId);
  const value = normalized(requested);
  const permitted = [manifest.systemId, ...(manifest.navigation?.aliases || [])].map(normalized);
  if (!value || !permitted.includes(value)) throw new TypeError('Nicht deklarierter Systemzustandsnamensraum.');
  return value;
}

let selectionContext = null;
/** Detect selection changes without disclosing another owner's fields or items. */
export function readSystemSelectionContext() {
  const source = S.selection;
  const signature = JSON.stringify(source || {});
  if (!selectionContext || selectionContext.source !== source || selectionContext.signature !== signature) {
    selectionContext = { source, signature, token: Object.freeze({}) };
  }
  return selectionContext.token;
}

/** Explicit legacy field claims preserve compatibility without exposing global selection. */
export function createOwnerSelectionFields(manifest, config, isDisposed) {
  if (config.legacyFields !== undefined && !Array.isArray(config.legacyFields)) throw new TypeError('Auswahlfelder muessen eine Liste sein.');
  const owner = manifest.systemId;
  const namespace = systemStateNamespace(manifest, config.namespace);
  const keys = [...new Set([config.primary, config.multiple, ...(config.legacyFields || [])].filter(Boolean))];
  for (const key of keys) {
    if (typeof key !== 'string' || !identifier.test(key) || forbidden.has(key)
      || !normalized(key).startsWith(namespace)) throw new TypeError('Ungueltiges eigenes Auswahlfeld.');
    if (claims.has(key) && claims.get(key).owner !== owner) throw new TypeError('Auswahlfeld gehoert einem anderen System.');
  }
  for (const key of keys) {
    const previous = claims.get(key);
    claims.set(key, { owner, count: (previous?.count || 0) + 1 });
  }
  let cached = null;
  function legacy() {
    if (isDisposed()) return Object.freeze({});
    const selection = S.selection || {};
    const result = {};
    for (const key of keys) if (selection[key] !== undefined) result[key] = selection[key];
    result.items = (selection.items || []).filter(item => item.owner === owner);
    const signature = JSON.stringify(result);
    if (!cached || cached.source !== selection || cached.signature !== signature) {
      cached = { source: selection, signature, value: freeze(cloneSystemData(result)) };
    }
    return cached.value;
  }
  function patchLegacy(patch) {
    if (isDisposed()) return false;
    const safe = cloneSystemData(patch);
    if (!safe || typeof safe !== 'object' || Array.isArray(safe)) throw new TypeError('Ungueltige Auswahl.');
    for (const key of Object.keys(safe)) if (key !== 'items' && !keys.includes(key)) throw new TypeError('Fremdes Auswahlfeld.');
    if (safe.items !== undefined && (!Array.isArray(safe.items)
      || safe.items.some(item => !item || item.owner !== owner || typeof item.id !== 'string' || typeof item.kind !== 'string'))) {
      throw new TypeError('Fremde Auswahlobjekte.');
    }
    S.selection ||= {};
    for (const key of keys) if (Object.hasOwn(safe, key)) S.selection[key] = safe[key];
    if (safe.items) S.selection.items = [
      ...(S.selection.items || []).filter(item => item.owner !== owner), ...safe.items
    ];
    cached = null;
    return true;
  }
  return Object.freeze({ legacy, patchLegacy, dispose() {
    for (const key of keys) {
      const claim = claims.get(key);
      if (claim?.owner !== owner) continue;
      if (claim.count > 1) claims.set(key, { owner, count: claim.count - 1 });
      else claims.delete(key);
    }
    cached = null;
  } });
}
